import { TagEntity } from "@citychallenge/api-public";
import { escapeRegex } from "@citychallenge/common";

import { observable, action, computed } from "mobx";
import { debounce } from "@citychallenge/common";
import { ArticleModel } from "./article.model";
import { Translation } from "../i18n";

export class TranslatableTag {
  public id!: string;
  public name!: string;

  public translation?: Translation;

  @observable
  public active = false;

  @action
  public toggleActive = () => {
    this.active = !this.active;
  };

  constructor(data: { id: string; name: string; translation?: Translation }) {
    Object.assign(this, data);
  }
}

export class ArticleStore {
  @observable
  public articles: ArticleModel[];

  @action
  public setArticles(articles: ArticleModel[]) {
    this.articles = articles;
  }

  constructor(articles: ArticleModel[] = []) {
    this.articles = articles;
  }

  public findById(articleId: string) {
    return this.articles.find(({ id }) => {
      return id === articleId;
    });
  }

  @observable
  public searchFilter = "";

  @action
  public setSearchFilter = (value: string) => {
    this.searchFilter = value;
    this.resetTags();
  };

  public setSearchFilterDebounced = debounce(this.setSearchFilter, 300);

  public resetSearchFilter = () => {
    this.setSearchFilter("");
  };

  @computed
  public get filteredArticles() {
    const { activeTags, articles } = this;

    if (this.searchFilter.length) {
      const regex = new RegExp(escapeRegex(this.searchFilter), "i");
      return articles.filter((article) => article.contains(regex));
    }

    if (activeTags.length) {
      return articles.filter((article) => {
        for (const { id } of activeTags) {
          if (article.tagIds.includes(id)) {
            return true;
          }
        }
        return false;
      });
    }

    return this.articles;
  }

  @computed
  public get tags(): TranslatableTag[] {
    const allTags: { [key: string]: TagEntity } = {};

    for (const { tags } of this.articles) {
      if (!tags) continue;
      for (const tag of tags) {
        if (!tag.id || tag.id in allTags) continue;
        allTags[tag.id] = tag;
      }
    }

    return Object.values(allTags).map((tag, index) => {
      return new TranslatableTag({
        id: tag.id || `${index}`,
        name: tag.name,
      });
    });
  }

  @computed
  public get activeTags() {
    return this.tags.filter((t) => t.active);
  }

  @action
  public resetTags = () => {
    for (const tag of this.tags) {
      tag.active = false;
    }
  };
}
